import { useState } from "react";
import { X } from "lucide-react";
import logo from "../Assests/ll.webp";
import { createEnquiry, sendEnquiryToWhatsApp } from "../services/siteService";
import { PROGRAM_OPTIONS } from "../data/siteContent";

export default function PopupForm({ onClose }) {
  const [formData, setFormData] = useState({
    parentName: "",
    childName: "",
    childAge: "",
    phoneNumber: "",
    email: "",
    program: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [whatsAppUrl, setWhatsAppUrl] = useState("");
  const [isWhatsAppBlocked, setIsWhatsAppBlocked] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.parentName.trim()) newErrors.parentName = "Parent name is required";
    if (!formData.childName.trim()) newErrors.childName = "Child name is required";
    if (!formData.childAge) newErrors.childAge = "Child age is required";
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) {
      newErrors.phoneNumber = "Enter a valid 10-digit number";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.program) newErrors.program = "Please select a program";
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const enquiryPayload = { ...formData, source: "popup" };

    setIsSubmitting(true);
    setSubmitError("");

    try {
      await createEnquiry(enquiryPayload);
      const whatsappResult = await sendEnquiryToWhatsApp(enquiryPayload);
      setWhatsAppUrl(whatsappResult.url);
      setIsWhatsAppBlocked(!whatsappResult.opened && Boolean(whatsappResult.url));
      localStorage.setItem("popupSubmitted", "true");
      setIsSubmitting(false);
      setSubmitted(true);

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setIsSubmitting(false);
      setSubmitError(error.message || "Unable to submit enquiry.");
    }
  };

  if (submitted) {
    return (
      <div className="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✓</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
          <p className="text-gray-600 text-sm">We'll contact you shortly.</p>
          {isWhatsAppBlocked && whatsAppUrl ? (
            <a
              href={whatsAppUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex mt-4 rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-600"
            >
              Open WhatsApp
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
      >
        <X size={20} />
      </button>

      <div className="text-center mb-6">
       <div className="inline-flex items-center justify-center w-auto h-12  rounded-2xl mb-3 shadow-lg">
            <img src={logo} alt="Kiddin Logo" className="w-full h-full object-cover" />
          </div>   
        <h3 className="text-xl font-bold text-gray-900">Quick Enquiry</h3>
        <p className="text-xs text-gray-500 mt-1">Fill this form to get more information</p>
      </div>

      <div className="space-y-3">
        {submitError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {submitError}
          </div>
        )}
        <div>
          <input
            name="parentName"
            value={formData.parentName}
            onChange={handleChange}
            placeholder="Parent Name *"
            className={`w-full bg-gray-50 border ${errors.parentName ? 'border-red-500' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition`}
          />
          {errors.parentName && <p className="mt-1 text-xs text-red-500">{errors.parentName}</p>}
        </div>

        <div>
          <input
            name="childName"
            value={formData.childName}
            onChange={handleChange}
            placeholder="Child Name *"
            className={`w-full bg-gray-50 border ${errors.childName ? 'border-red-500' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition`}
          />
          {errors.childName && <p className="mt-1 text-xs text-red-500">{errors.childName}</p>}
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <select
              name="childAge"
              value={formData.childAge}
              onChange={handleChange}
              className={`w-full bg-gray-50 border ${errors.childAge ? 'border-red-500' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition`}
            >
              <option value="">Age *</option>
              <option value="1.5 - 2 Years">1.5 - 2 Years</option>
              <option value="2 - 3 Years">2 - 3 Years</option>
              <option value="3 - 4 Years">3 - 4 Years</option>
              <option value="4 - 5 Years">4 - 5 Years</option>
              <option value="5 - 6 Years">5 - 6 Years</option>
            </select>
            {errors.childAge && <p className="mt-1 text-xs text-red-500">{errors.childAge}</p>}
          </div>

          <div className="flex-1">
            <input
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Phone *"
              className={`w-full bg-gray-50 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition`}
            />
            {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>}
          </div>
        </div>

        <div>
          <input
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            placeholder="Email Address *"
            className={`w-full bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition`}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
        </div>

        <div>
          <select
            name="program"
            value={formData.program}
            onChange={handleChange}
            className={`w-full bg-gray-50 border ${errors.program ? 'border-red-500' : 'border-gray-200'} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition`}
          >
            <option value="">Select Program *</option>
            {PROGRAM_OPTIONS.map((program) => (
              <option key={program} value={program}>{program}</option>
            ))}
          </select>
          {errors.program && <p className="mt-1 text-xs text-red-500">{errors.program}</p>}
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold py-2.5 rounded-xl transition-all duration-300 disabled:opacity-50 mt-2"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </div>
          ) : (
            "Submit Enquiry"
          )}
        </button>

        <p className="text-center text-[10px] text-gray-400 mt-3">
          We respect your privacy. No spam, ever.
        </p>
      </div>
    </div>
  );
}
